import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { Plus, Trash2, Save, Sparkles, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import './OpenBoxBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OpenBoxBuilder() {
  const { user } = React.useContext(AuthContext);
  const [gameData, setGameData] = useState({
    title: '',
    timer_setting: 'off',
    boxes: [{ content: '', image_url: null, emoji: '🎁', nested_boxes: [] }]
  });
  
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiNumBoxes, setAiNumBoxes] = useState(6);
  const [generating, setGenerating] = useState(false);
  
  const handleImageUpload = async (file, boxIndex) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      const newBoxes = [...gameData.boxes];
      newBoxes[boxIndex].image_url = response.data.url;
      setGameData({ ...gameData, boxes: newBoxes });
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    }
  };
  
  const addBox = () => {
    setGameData({
      ...gameData,
      boxes: [...gameData.boxes, { content: '', image_url: null, emoji: '🎁', nested_boxes: [] }]
    });
  };
  
  const deleteBox = (index) => {
    const newBoxes = gameData.boxes.filter((_, i) => i !== index);
    setGameData({ ...gameData, boxes: newBoxes });
  };
  
  const updateBox = (index, field, value) => {
    const newBoxes = [...gameData.boxes];
    newBoxes[index][field] = value;
    setGameData({ ...gameData, boxes: newBoxes });
  };
  
  const addNestedBox = (parentIndex) => {
    const newBoxes = [...gameData.boxes];
    newBoxes[parentIndex].nested_boxes.push({ content: '', emoji: '🎁' });
    setGameData({ ...gameData, boxes: newBoxes });
  };
  
  const updateNestedBox = (parentIndex, nestedIndex, field, value) => {
    const newBoxes = [...gameData.boxes];
    newBoxes[parentIndex].nested_boxes[nestedIndex][field] = value;
    setGameData({ ...gameData, boxes: newBoxes });
  };
  
  const deleteNestedBox = (parentIndex, nestedIndex) => {
    const newBoxes = [...gameData.boxes];
    newBoxes[parentIndex].nested_boxes = newBoxes[parentIndex].nested_boxes.filter((_, i) => i !== nestedIndex);
    setGameData({ ...gameData, boxes: newBoxes });
  };
  
  const saveGame = async () => {
    if (!gameData.title) {
      toast.error('Please enter a game title');
      return;
    }
    if (gameData.boxes.some(box => !box.content)) {
      toast.error('Please fill in all box contents');
      return;
    }
    try {
      await axios.post(`${API}/open-box`, gameData, { withCredentials: true });
      toast.success('Game saved successfully!');
      setGameData({
        title: '',
        timer_setting: 'off',
        boxes: [{ content: '', image_url: null, emoji: '🎁', nested_boxes: [] }]
      });
    } catch (error) {
      console.error('Failed to save game:', error);
      toast.error('Failed to save game');
    }
  };
  
  const generateAIBoxes = async () => {
    if (!aiTopic) {
      toast.error('Please enter a topic');
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-open-box`, {
        topic: aiTopic,
        num_boxes: aiNumBoxes
      }, { withCredentials: true });
      toast.success('AI Boxes generated!');
      setAiDialogOpen(false);
      setAiTopic('');
      setGameData({ ...gameData, boxes: response.data.boxes.map(b => ({ ...b, image_url: null, nested_boxes: [] })) });
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate boxes');
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="openbox-builder-page">
      <div className="builder-container">
        <div className="builder-header">
          <h1>🎁 Open the Box Builder</h1>
          <div className="header-actions">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild>
                <Button className="ai-btn"><Sparkles size={20} />AI Box Generator</Button>
              </DialogTrigger>
              <DialogContent className="ai-dialog">
                <DialogHeader><DialogTitle>AI Box Generator</DialogTitle></DialogHeader>
                <div className="ai-form">
                  <div className="form-group">
                    <Label>Topic</Label>
                    <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g., Space, Animals..." />
                  </div>
                  <div className="form-group">
                    <Label>Number of Boxes</Label>
                    <Input type="number" min="3" max="12" value={aiNumBoxes} onChange={(e) => setAiNumBoxes(parseInt(e.target.value))} />
                  </div>
                  <Button onClick={generateAIBoxes} disabled={generating} className="generate-btn">
                    {generating ? 'Generating...' : 'Generate Boxes'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={saveGame} className="save-btn"><Save size={20} />Save Game</Button>
          </div>
        </div>
        
        <div className="game-settings">
          <Input value={gameData.title} onChange={(e) => setGameData({ ...gameData, title: e.target.value })} placeholder="Game Title" className="title-input" />
          <div className="setting-group">
            <Label>Timer</Label>
            <Select value={gameData.timer_setting} onValueChange={(value) => setGameData({ ...gameData, timer_setting: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">OFF</SelectItem>
                <SelectItem value="30s">30 seconds</SelectItem>
                <SelectItem value="1min">1 minute</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="boxes-section">
          <h2>Boxes ({gameData.boxes.length})</h2>
          <div className="boxes-list">
            {gameData.boxes.map((box, index) => (
              <div key={index} className="box-card">
                <div className="box-header">
                  <span className="box-number">Box {index + 1} {box.emoji}</span>
                  {gameData.boxes.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => deleteBox(index)} className="delete-btn">
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>
                <div className="box-content">
                  <Textarea value={box.content} onChange={(e) => updateBox(index, 'content', e.target.value)} placeholder="Box content (question or fact)" rows={3} />
                  <div className="box-controls">
                    <Input value={box.emoji} onChange={(e) => updateBox(index, 'emoji', e.target.value)} placeholder="Emoji" className="emoji-input" />
                    <input type="file" accept="image/*" id={`box-img-${index}`} onChange={(e) => handleImageUpload(e.target.files[0], index)} style={{ display: 'none' }} />
                    <label htmlFor={`box-img-${index}`} className="upload-btn-sm">Add Image</label>
                  </div>
                  {box.image_url && <img src={box.image_url} alt="Box" className="box-preview-img" />}
                  
                  <div className="nested-boxes-section">
                    <Button variant="outline" size="sm" onClick={() => addNestedBox(index)} className="add-nested-btn">
                      <Plus size={16} />Add Nested Box
                    </Button>
                    {box.nested_boxes.map((nested, nIndex) => (
                      <div key={nIndex} className="nested-box">
                        <Input value={nested.content} onChange={(e) => updateNestedBox(index, nIndex, 'content', e.target.value)} placeholder="Nested box content" />
                        <Button variant="ghost" size="sm" onClick={() => deleteNestedBox(index, nIndex)}><Trash2 size={14} /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={addBox} className="add-box-btn"><Plus size={20} />Add Box</Button>
        </div>
      </div>
    </div>
  );
}

export default OpenBoxBuilder;